import type { CSSProperties } from "react";

import { usePicture } from "@/api/queries";
import { initials } from "@/lib/format";
import { useObjectUrl } from "@/lib/useObjectUrl";

import s from "./ui.module.css";

export type AvatarProps = {
  name: string;
  /** With both, the person's profile picture is shown once it has loaded; initials until then and otherwise. */
  userId?: string;
  pictureVersion?: string;
  /** Side in pixels: 32 by default; 24, 40, 48 and 96 are the other standard sizes. */
  size?: number;
  /** A picture to show instead, such as a preview of one about to be saved. */
  src?: string;
};

/** The standard sizes have a class each; any other size is set inline. */
const SIZE_CLASS: Record<number, string | undefined> = { 24: s.avatar24, 32: undefined, 40: s.avatar40, 48: s.avatar48, 96: s.avatar96 };

function sizeStyle(size: number): CSSProperties | undefined {
  return size in SIZE_CLASS ? undefined : { width: size, height: size, fontSize: Math.round(size * 0.375) };
}

function sizeClass(size: number): string | undefined {
  const extra = SIZE_CLASS[size];
  return extra ? `${s.avatar} ${extra}` : s.avatar;
}

/**
 * A person's picture or, without one, their initials, in the same circle. The image has an
 * empty alt and the initials are hidden from screen readers: the name is always beside it.
 */
export function Avatar({ name, userId, pictureVersion, size = 32, src }: AvatarProps) {
  if (src) return <img className={sizeClass(size)} src={src} alt="" width={size} height={size} style={sizeStyle(size)} />;
  if (userId && pictureVersion) return <PictureAvatar name={name} userId={userId} pictureVersion={pictureVersion} size={size} />;
  return <Initials name={name} size={size} />;
}

function Initials({ name, size }: { name: string; size: number }) {
  return <span className={sizeClass(size)} style={sizeStyle(size)} aria-hidden>{initials(name)}</span>;
}

function PictureAvatar({ name, userId, pictureVersion, size }: { name: string; userId: string; pictureVersion: string; size: number }) {
  const picture = usePicture(userId, pictureVersion);
  const url = useObjectUrl(picture.data);
  if (!url) return <Initials name={name} size={size} />;
  return <img className={sizeClass(size)} src={url} alt="" width={size} height={size} style={sizeStyle(size)} data-picture={pictureVersion} />;
}
